import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthSession } from './types';

interface AuthState {
  session: AuthSession | null;
  /**
   * Why the session ended, when it ended for a reason worth telling the user about —
   * slice-3560 (ADR-0046). Read by `LoginPage`; `null` for an ordinary sign-out.
   *
   * <p>Deliberately NOT persisted (`partialize` keeps `session` only): the message exists to
   * explain the redirect that just happened in this tab, not to greet the next visit.
   */
  sessionExpiredReason: string | null;
}

/** Renewal output — the subset of `RenewResponse` that lands on the stored session. */
export interface SessionRenewal {
  token: string;
  expiresAt?: string | undefined;
  sessionExpiresAt?: string | undefined;
  kiosk?: boolean | undefined;
}

interface AuthActions {
  setSession: (s: AuthSession) => void;
  /**
   * Slide the stored session forward after a successful `POST /api/auth/renew` —
   * slice-3560 (ADR-0046). A no-op when signed out, so a renewal that resolves after the
   * user has logged out cannot resurrect the session.
   */
  applyRenewal: (r: SessionRenewal) => void;
  /**
   * Drop the session. `reason` is the message the login page shows; omit it for a
   * deliberate sign-out. Before slice-3560 this was a *silent* clear on any 401 — the
   * operator was redirected to `/login` mid-keystroke with no explanation at all.
   */
  clear: (reason?: string) => void;
  dismissExpiredReason: () => void;
}

/**
 * Auth store. Persists to localStorage so a refresh keeps the user logged in.
 *
 * <p>slice-3560 (ADR-0046): the session is now idle-based with sliding renewal —
 * `useSessionExpiry` renews the token from `session.expiresAt` while the user is active,
 * warns before the tenant's idle limit, and calls `clear(reason)` on expiry so the user is
 * told what happened and returned to the route they were on.
 */
export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      session: null,
      sessionExpiredReason: null,
      setSession: (session) => set({ session, sessionExpiredReason: null }),
      applyRenewal: (r) =>
        set((s) =>
          s.session
            ? {
                session: {
                  ...s.session,
                  token: r.token,
                  expiresAt: r.expiresAt,
                  sessionExpiresAt: r.sessionExpiresAt,
                  kiosk: r.kiosk ?? s.session.kiosk,
                },
              }
            : {},
        ),
      clear: (reason) =>
        set({ session: null, sessionExpiredReason: typeof reason === 'string' ? reason : null }),
      dismissExpiredReason: () => set({ sessionExpiredReason: null }),
    }),
    {
      name: 'ph10-auth',
      partialize: (s) => ({ session: s.session }),
    },
  ),
);

/** Convenience selector — true when a session is present. */
export const isAuthenticated = (): boolean => useAuthStore.getState().session !== null;
